import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ModuleFrame, { Chip, KV } from '../ModuleFrame';
import { withApp } from '../AppSelector';
import { InstanceLinkSelect } from '../InstanceLink';
import { apiPost, isBackendConfigured } from '../../utils/backendApi';
import { friendlyError } from '../../data/entities';

// Ready to ship › Build on the instance — the Lab → Dev Console handoff
// (decision lab-is-the-studio, audit P0-2). Everything the Lab produced
// (concept, accepted stories and ideas, agents, live documents, browser
// test reports) goes down to the linked instance as the app bundle, and
// the kit's Dev Console starts the first build from it. One primary
// button; the card names what is missing when it cannot be pressed.
// Build state merges the plane's request (app.build) with what the kit
// reports back every five minutes (monitor.build) — `ops.data.build`.

const STATE = {
  requested: ['info', 'Requested — waiting for the instance to pick it up'],
  underway: ['info', 'Building in the Dev Console'],
  built: ['ok', 'First build done'],
  blocked: ['err', 'Blocked'],
};

export default function BuildHandoff({ org, app, instance, instances, ops, next, canBuild, canLink, onTab }) {
  const [state, setState] = useState('');
  const d = ops.data || {};
  const build = d.build || app.build || null;
  const lab = d.lab || {};
  const concept = lab.concept?.accepted || null;
  const stories = lab.stories || null;
  // accepted = untested + verified + failing-but-accepted (appOperate labSummary).
  const storiesReady = stories ? (stories.untested || 0) + (stories.verified || 0) + (stories.failing || 0) : 0;
  const agents = d.agents || [];
  const liveDocs = (d.docs || []).filter((x) => x.status === 'live');
  const sync = app.links?.instanceSync || null;

  // What stops the button, in the order a person would fix it.
  let blocker = null;
  if (!app.name || !app.goal) blocker = { text: 'Say what the app is first (name and goal).', phase: 'design' };
  else if (!agents.length) blocker = { text: 'Attach an agent in Lab › Design — the instance needs one to answer.', phase: 'design' };
  else if (!instance) blocker = { text: 'Pick the hosted instance under "Where it lands", or launch one in DevOps.' };
  else if (instance.status !== 'live') blocker = { text: `${instance.name || instance.slug} is ${instance.status || 'not live'} — the build starts once it is live.` };
  const underway = build && ['requested', 'underway'].includes(build.state);
  // The design reads the build before the build reads the design: when the
  // instance's Dev Console has moved since the last catch-up, say so here.
  const dev = d.dev || null;
  const behind = dev?.behind === true;

  const start = async () => {
    setState('busy');
    try {
      await apiPost(`/apps/${org.id}/${app.id}/sync-instance`, { build: true });
      setState('done');
      ops.reload?.();
    } catch (ex) {
      setState(friendlyError(ex));
      ops.reload?.();
    }
  };

  const devUrl = d.instance?.devUrl || instance?.urls?.dev || (instance?.urls?.app ? `${instance.urls.app.replace(/\/$/, '')}/dev` : null);
  const tone = build?.state ? STATE[build.state]?.[0] || 'muted' : 'muted';

  return (
    <ModuleFrame.Panel
      title="Build on the instance"
      meta="hand what the Lab produced to the instance's Dev Console, which builds the screens"
      actions={build?.state ? <Chip tone={tone}>{STATE[build.state]?.[1] || build.state}</Chip> : null}
    >
      <div className="bh-grid">
        <div>
          <h4 className="bh-h">What goes down</h4>
          <KV rows={[
            ['Concept', concept ? `"${concept.title}" v${concept.version}` : <span className="mf-muted">none accepted — the build uses the name and goal <button type="button" className="cs-btn-link" onClick={() => onTab('lab', { phase: 'concept' })}>(talk it through)</button></span>],
            ['Stories', storiesReady ? `${storiesReady} accepted${stories?.verified ? ` · ${stories.verified} verified in a browser` : ''}` : <span className="mf-muted">none yet — the build has only the concept to go on <button type="button" className="cs-btn-link" onClick={() => onTab('lab', { phase: 'stories' })}>(write some)</button></span>],
            ['Agents', agents.length ? agents.map((a) => a.name).join(', ') : <span className="cs-form-error">none attached</span>],
            ['Documents', `${liveDocs.length} live`],
            ['Test reports', lab.lastTest ? `last ${lab.lastTest.verdict} on ${lab.lastTest.env || 'the app'}` : '—'],
          ]} />
        </div>
        <div>
          <h4 className="bh-h">Where it lands</h4>
          <div className="ab-next-actions" style={{ marginBottom: 8 }}>
            <InstanceLinkSelect orgId={org.id} app={app} instances={instances} canEdit={canLink} />
            {!instance && <Link to={withApp(`/orgs/${org.id}/deployments`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Launch one in DevOps</Link>}
          </div>
          {instance && (
            <KV rows={[
              ['Instance', `${instance.name || instance.slug} · ${instance.status || '—'}`],
              ['Address', instance.urls?.app ? <a href={instance.urls.app} target="_blank" rel="noreferrer">{instance.urls.app}</a> : '—'],
              ['Last handoff', sync ? `${new Date(sync.at).toLocaleString()} · ${sync.agents} agent${sync.agents === 1 ? '' : 's'}, ${sync.docs} document${sync.docs === 1 ? '' : 's'}${sync.stories != null ? `, ${sync.stories} stor${sync.stories === 1 ? 'y' : 'ies'}` : ''}` : 'never'],
              ...(build?.state ? [['Build', <>
                {STATE[build.state]?.[1] || build.state}
                {build.reason ? <span className="cs-form-error" style={{ display: 'block' }}>{build.reason}</span> : null}
                {build.requestedBy ? <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>requested by {build.requestedBy}{build.requestedAt ? ` · ${new Date(build.requestedAt).toLocaleString()}` : ''}</span> : null}
                {build.state === 'requested' && build.kitKnows === false ? <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>this instance runs an older kit — the design was synced; open its Dev Console and start the build there</span> : null}
              </>]] : []),
            ]} />
          )}
        </div>
      </div>
      <div className="ab-next-actions bh-actions">
        {canBuild && isBackendConfigured() ? (
          <button type="button" className={`cs-btn cs-btn-sm ${underway ? 'cs-btn-outline' : 'cs-btn-primary'}`} disabled={!!blocker || state === 'busy'} title={blocker?.text || (underway ? 'Send the latest Lab work down again; the build picks it up' : 'Send to the Dev Console and start the build')} onClick={start}>
            {state === 'busy' ? 'Sending… (about a minute)' : underway ? 'Send the latest Lab work again' : build?.state === 'built' ? 'Build again from the Lab' : 'Send to the Dev Console and start the build'}
          </button>
        ) : <span className="cs-help">An organization admin starts the build.</span>}
        {devUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={devUrl} target="_blank" rel="noreferrer">Open the Dev Console ↗</a>}
        {build?.prUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={build.prUrl} target="_blank" rel="noreferrer">Pull request ↗</a>}
        {build?.previewUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={build.previewUrl} target="_blank" rel="noreferrer">Preview ↗</a>}
        {blocker && <span className="cs-help bh-blocker">{blocker.text}{blocker.phase ? <> <button type="button" className="cs-btn-link" onClick={() => onTab('lab', { phase: blocker.phase })}>open →</button></> : null}</span>}
        {state === 'done' && <span className="mf-muted">Sent. The Dev Console has it{build?.state === 'blocked' ? '' : ' — this card updates as it reports back'}.</span>}
        {state && state !== 'busy' && state !== 'done' && <span className="cs-form-error">{state}</span>}
      </div>
      {behind && (
        <p className="cs-help bh-behind" style={{ margin: '8px 0 0' }}>
          {dev.kitKnows
            ? `${dev.devSessions || 0} Dev Console session${dev.devSessions === 1 ? '' : 's'} on the instance${dev.planeTurns ? ` and ${dev.planeTurns} conversation turns here` : ''} since the ${dev.neverCaughtUp ? 'design was made' : 'last catch-up'}`
            : `${dev.planeTurns || 0} conversation turns about this app since the ${dev.neverCaughtUp ? 'design was made' : 'last catch-up'}`}
          {' '}— the design has not read them. <button type="button" className="cs-btn-link" onClick={() => onTab('lab', { phase: 'concept' })}>Catch up first →</button>
        </p>
      )}
      {next?.stage === 'ship' && next.label !== 'Build on the instance' && next.label !== 'Build underway' && (
        <p className="cs-help" style={{ margin: '8px 0 0' }}>Next: {next.label} — {next.text}</p>
      )}
    </ModuleFrame.Panel>
  );
}
